import type Phaser from 'phaser';
import { PALETTE } from '../theme';

/**
 * Ein Sonarschirm als Hintergrund.
 *
 * Nichts sagt so schnell „hier geht es um Suchen im Dunkeln" wie ein
 * kreisender Sonarstrahl. Er kostet nichts — ein paar Kreise, eine rotierende
 * Keule und ein paar Punkte, die aufleuchten und verblassen —, traegt aber die
 * ganze Menuestimmung.
 *
 * Die Punkte sind bewusst bedeutungslos: Sie stellen nichts dar und lassen
 * sich nicht anklicken. Es geht um Atmosphaere, nicht um Information.
 */

interface Blip {
  readonly angle: number;
  readonly radius: number;
  /** Wann der Strahl zuletzt darueber lief. */
  litAt: number;
}

export class SonarScope {
  private readonly rings: Phaser.GameObjects.Graphics;
  private readonly sweep: Phaser.GameObjects.Graphics;
  private readonly blips: Blip[] = [];

  private angle = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly centreX: number,
    private readonly centreY: number,
    private readonly radius: number,
  ) {
    this.rings = scene.add.graphics().setDepth(-20);
    this.sweep = scene.add.graphics().setDepth(-19);

    this.drawRings();

    // Eine Handvoll Kontakte, zufaellig verteilt. Sie bleiben, solange die
    // Szene lebt — so wirkt der Schirm wie ein stehendes Bild und nicht wie
    // ein Feuerwerk.
    for (let i = 0; i < 9; i++) {
      this.blips.push({
        angle: Math.random() * Math.PI * 2,
        radius: (0.25 + Math.random() * 0.7) * radius,
        litAt: -999,
      });
    }
  }

  /** Die feststehenden Ringe und Kreuzlinien. */
  private drawRings(): void {
    this.rings.clear();
    this.rings.lineStyle(1, PALETTE.panelEdge, 0.5);

    for (const fraction of [0.25, 0.5, 0.75, 1]) {
      this.rings.strokeCircle(this.centreX, this.centreY, this.radius * fraction);
    }

    this.rings.beginPath();
    this.rings.moveTo(this.centreX - this.radius, this.centreY);
    this.rings.lineTo(this.centreX + this.radius, this.centreY);
    this.rings.moveTo(this.centreX, this.centreY - this.radius);
    this.rings.lineTo(this.centreX, this.centreY + this.radius);
    this.rings.strokePath();
  }

  /**
   * Dreht den Strahl weiter und laesst getroffene Kontakte aufleuchten.
   * `deltaMs` kommt aus der Szenenschleife.
   */
  update(deltaMs: number, nowMs: number): void {
    const previous = this.angle;
    // Eine Umdrehung in gut acht Sekunden — geduldig, nicht hektisch.
    this.angle = (this.angle + (deltaMs / 1000) * 0.75) % (Math.PI * 2);

    for (const blip of this.blips) {
      if (hasPassed(previous, this.angle, blip.angle)) blip.litAt = nowMs;
    }

    this.sweep.clear();

    // Die Keule: mehrere Speichen mit abnehmender Deckkraft hinter dem Strahl.
    const tail = 0.7;
    for (let i = 0; i < 14; i++) {
      const t = i / 14;
      const a = this.angle - t * tail;
      this.sweep.lineStyle(2, PALETTE.base, 0.18 * (1 - t));
      this.sweep.beginPath();
      this.sweep.moveTo(this.centreX, this.centreY);
      this.sweep.lineTo(
        this.centreX + Math.cos(a) * this.radius,
        this.centreY + Math.sin(a) * this.radius,
      );
      this.sweep.strokePath();
    }

    // Kontakte verblassen ueber gut drei Sekunden.
    for (const blip of this.blips) {
      const age = (nowMs - blip.litAt) / 3200;
      if (age > 1) continue;

      const alpha = (1 - age) ** 2 * 0.7;
      this.sweep.fillStyle(PALETTE.base, alpha);
      this.sweep.fillCircle(
        this.centreX + Math.cos(blip.angle) * blip.radius,
        this.centreY + Math.sin(blip.angle) * blip.radius,
        2.5,
      );
    }
  }

  /**
   * Die eigenen Zeichenobjekte.
   *
   * Szenen, die ihre Bedienelemente neu aufbauen, muessen den Schirm dabei
   * verschonen — sonst setzte der Strahl bei jedem Klick zurueck.
   */
  get objects(): readonly Phaser.GameObjects.GameObject[] {
    return [this.rings, this.sweep];
  }

  destroy(): void {
    this.rings.destroy();
    this.sweep.destroy();
  }
}

/**
 * Hat der Strahl zwischen zwei Bildern den Winkel `target` ueberstrichen?
 * Der Sprung ueber 0 muss dabei mitgedacht werden, sonst leuchten Kontakte
 * genau an der Nahtstelle nie auf.
 */
function hasPassed(from: number, to: number, target: number): boolean {
  if (from <= to) return target > from && target <= to;
  return target > from || target <= to;
}
